import cloneDeep from 'lodash/cloneDeep';
import { toArray } from '@/utils';
import Vue from 'vue';

const channelTemplate = {
  name: '',
  form: {
    pristine: true,
    dirty: false,
    valid: true,
    invalid: false,
    touched: false,
    untouched: true,
    validated: false,
  },
  formErrors: [],
  formFields: {},
  draft: {},
};

/**
 * Initialize the content editor
 */
const INIT_EDITOR = (state, content) => {
  state.id = content.id;
  state.relativeVersionId = content.relativeVersionId || null;
  state.entityPublishedVersionStartPublicationDate = content.entityPublishedVersionStartPublicationDate || null;
  state.entityPublished = content.entityPublished || false;
  state.entityScheduled = content.entityScheduled || false;
  state.hasBeenPublished = content.hasEverBeenPublished || false;
  state.type = content.type;
  state.versionPublished = content.versionPublished;
  state.isDirty = false;
  state.isDraft = content.isDraft;
  state.availableChannels = content.availableChannels;
  state.currentLanguage = content.currentLanguage || null;
  state.availableLanguages = toArray(content.languages);
  state.downloadUrl = content.downloadUrl || null;
  state.original = content;
  state.publicUrl = content.publicUrl || null;
  state.publicDraftUrl = content.publicDraftUrl || null;
  state.fieldsConfig = content.fieldsConfig || [];
  // Set channels initial values
  Object
    .entries(content.channels)
    .forEach(([channel, fields]) => {
      const template = cloneDeep(channelTemplate);
      template.name = channel;
      template.draft = cloneDeep(fields);
      Vue.set(state.channels, channel, template);
    });
  // Reset hook callbacks
  state.beforeSaveCallbacks = [];
  state.beforePublishCallbacks = [];
};

/**
 * Add a new channel to the content editor
 */
const ADD_CHANNEL = (state, { channel, fields }) => {
  const template = cloneDeep(channelTemplate);
  template.name = channel;
  template.draft = cloneDeep(fields);
  Vue.set(state.original.channels, channel, template.draft);
  Vue.set(state.channels, channel, template);
};

/**
 * Remove a channel from the content editor
 */
const REMOVE_CHANNEL = (state, channel) => {
  Vue.delete(state.channels, channel);
};

/**
 * Set the current channel in the content editor
 */
const SET_CURRENT_CHANNEL = (state, channel) => {
  state.currentChannel = channel;
  state.selectedPreviewUrl = null;
};

/**
 * Set form flags in the specified channel
 */
const SET_CHANNEL_FORM_FLAGS = (state, { channel, flags }) => {
  state.isDirty = flags.dirty;
  state.channels[channel].form = cloneDeep(flags);
};

/**
 * Set form errors in the specified channel
 */
const SET_CHANNEL_FORM_ERRORS = (state, { channel, errors }) => {
  state.channels[channel].formErrors = cloneDeep(errors);
};

/**
 * Set form fields in the specified channel
 */
const SET_CHANNEL_FORM_FIELDS = (state, { channel, fields }) => {
  state.channels[channel].formFields = cloneDeep(fields);
};

/**
 * Update field changes in channel draft
 */
const UPDATE_CHANNEL_DRAFT = (state, { channel, fields }) => {
  state.isDraft = true;
  state.isDirty = true;
  state.channels[channel].draft = { ...state.channels[channel].draft, ...cloneDeep(fields) };
};

/**
 * Updates original content
 */
const SET_CURRENT_CONTENT = (state, original) => {
  // Preserve current channel if not present in current content
  if (state.currentChannel in original.channels === false) {
    original.channels[state.currentChannel] = state.channels[state.currentChannel].draft;
  }
  state.original = cloneDeep(original);
  // Update fieldsConfig to overwrite field definition after save
  state.fieldsConfig = cloneDeep(original.fieldsConfig || []);
  // Update content public urls
  state.publicUrl = original.publicUrl || null;
  state.publicDraftUrl = original.publicDraftUrl || null;
  // Update versionable fields
  state.entityPublishedVersionStartPublicationDate = original.entityPublishedVersionStartPublicationDate || null;
  state.entityPublished = original.entityPublished || false;
  // Update channel fields
  Object
    .entries(original.channels)
    .forEach(([channel, fields]) => {
      const template = cloneDeep(channelTemplate);
      template.name = channel;
      template.draft = cloneDeep(fields);
      Vue.set(state.channels, channel, template);
    });
  state.isDirty = false;
};

/**
 * Publish the current content
 */
const PUBLISH_CONTENT = (state, {
  modificationDate, publishedDate, lastModificationStaffId, published,
}) => {
  state.original.publishedDate = publishedDate;
  state.original.modificationDate = modificationDate;
  state.original.lastModificationStaffId = lastModificationStaffId;
  state.original.versionPublished = published;
  state.versionPublished = published;
  state.entityPublished = published;
  state.isDraft = !published;
  if (!published) {
    state.entityPublishedVersionStartPublicationDate = null;
  }
};

/**
 * Toggle the editor between edition and preview modes
 */
const TOGGLE_PREVIEW_MODE = (state) => {
  state.isPreview = !state.isPreview;
};

const SET_SELECTED_PREVIEW_URL = (state, previewUrl) => {
  state.selectedPreviewUrl = previewUrl;
};

const TOGGLE_ADVANCED_EDITOR = (state) => {
  state.advancedMode = !state.advancedMode;
};

const ENABLE_ADVANCED_EDITOR = (state) => {
  state.advancedMode = true;
};

const ADD_BEFORE_SAVE_CALLBACK = (state, asyncFn) => {
  state.beforeSaveCallbacks = [
    ...state.beforeSaveCallbacks,
    asyncFn,
  ];
};

const ADD_BEFORE_PUBLISH_CALLBACK = (state, asyncFn) => {
  state.beforePublishCallbacks = [
    ...state.beforePublishCallbacks,
    asyncFn,
  ];
};

const MODIFY_AVAILABLE_LANGUAGES = (state, availableLanguages) => {
  state.availableLanguages = availableLanguages;
};

export default {
  ADD_CHANNEL,
  REMOVE_CHANNEL,
  INIT_EDITOR,
  PUBLISH_CONTENT,
  SET_CURRENT_CHANNEL,
  SET_CHANNEL_FORM_FLAGS,
  SET_CHANNEL_FORM_ERRORS,
  SET_CHANNEL_FORM_FIELDS,
  SET_CURRENT_CONTENT,
  UPDATE_CHANNEL_DRAFT,
  TOGGLE_PREVIEW_MODE,
  SET_SELECTED_PREVIEW_URL,
  TOGGLE_ADVANCED_EDITOR,
  ENABLE_ADVANCED_EDITOR,
  ADD_BEFORE_SAVE_CALLBACK,
  ADD_BEFORE_PUBLISH_CALLBACK,
  MODIFY_AVAILABLE_LANGUAGES,
};
